import React, { useState, useEffect } from 'react';
import { callReportsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  PhoneIcon,
  ClockIcon,
  TrendingUpIcon,
  BarChart3Icon,
  CalendarIcon,
  PhoneCallIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  DownloadIcon,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface CallReport {
  id: number;
  event: string;
  timestamp: string;
  direction: 'inbound' | 'outbound';
  uniqueid: string;
  caller_num?: string;
  caller_name?: string;
  callee_did?: string;
  duration: number;
  billsec: number;
  ring_time: number;
  disposition: 'ANSWERED' | 'NO ANSWER' | 'BUSY' | 'FAILED' | 'CONGESTION';
  service?: {
    id: number;
    name: string;
    extension: string;
  };
}

interface CallReportsResponse {
  reports: CallReport[];
  total: number;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface Statistics {
  total_calls: number;
  answered_calls: number;
  answer_rate: number;
  total_duration_seconds: number;
  average_duration_seconds: number;
}

interface DashboardStats {
  calls_this_month: number;
  calls_this_year: number;
  calls_with_duration: number;
  total_duration_month_seconds: number;
  total_duration_year_seconds: number;
  answered_calls_month: number;
  answered_calls_year: number;
  answer_rate_month: number;
  answer_rate_year: number;
  current_month: string;
  current_year: number;
}

const CallReportsPage: React.FC = () => {
  const [reports, setReports] = useState<CallReport[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    direction: '',
    disposition: '',
    start_date: '',
    end_date: '',
    service_id: ''
  });

  const [userServices, setUserServices] = useState<Array<{id: number, name: string, extension: string}>>([]);

  const exportToPDF = async () => {
    try {
      // Mostrar loading
      const loadingToast = toast.loading('Gerando relatório PDF...');

      // Criar novo documento PDF
      const doc = new jsPDF();

      // Configurações
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 20;

      // Cabeçalho elegante
      doc.setFillColor(59, 130, 246); // blue-500
      doc.rect(0, 0, pageWidth, 40, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('RELATÓRIO DE LIGAÇÕES PABX', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Sistema de Relatórios de Chamadas', pageWidth / 2, 30, { align: 'center' });

      yPosition = 50;

      // Data de geração
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(10);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth / 2, yPosition, { align: 'center' });

      yPosition += 20;

      // Dashboard - Estatísticas com melhor design
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('DASHBOARD DE LIGAÇÕES', 20, yPosition);

      yPosition += 15;

      // Criar cards de estatísticas
      const statsY = yPosition;

      // Card 1: Ligações do Mês
      doc.setFillColor(240, 240, 240);
      doc.rect(20, statsY, 80, 25, 'F');
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Ligações do Mês', 25, statsY + 8);
      doc.setFontSize(14);
      doc.text(`${(dashboardStats?.calls_this_month || 0).toLocaleString('pt-BR')}`, 25, statsY + 18);
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Atendidas: ${dashboardStats?.answered_calls_month || 0}`, 25, statsY + 23);

      // Card 2: Ligações do Ano
      doc.setFillColor(240, 240, 240);
      doc.rect(110, statsY, 80, 25, 'F');
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Ligações do Ano', 115, statsY + 8);
      doc.setFontSize(14);
      doc.text(`${(dashboardStats?.calls_this_year || 0).toLocaleString('pt-BR')}`, 115, statsY + 18);
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Atendidas: ${dashboardStats?.answered_calls_year || 0}`, 115, statsY + 23);

      yPosition += 35;

      // Card 3: Ligações Efetivas
      doc.setFillColor(240, 240, 240);
      doc.rect(20, yPosition, 80, 25, 'F');
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Ligações Efetivas', 25, yPosition + 8);
      doc.setFontSize(14);
      doc.text(`${(dashboardStats?.calls_with_duration || 0).toLocaleString('pt-BR')}`, 25, yPosition + 18);
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Com duração >5s', 25, yPosition + 23);

      // Card 4: Tempo Total
      doc.setFillColor(240, 240, 240);
      doc.rect(110, yPosition, 80, 25, 'F');
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Tempo Total (Mês)', 115, yPosition + 8);
      doc.setFontSize(14);
      doc.text(`${formatDuration(dashboardStats?.total_duration_month_seconds || 0)}`, 115, yPosition + 18);
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Duração das conversas', 115, yPosition + 23);

      yPosition += 40;

      // Preparar filtros aplicados
      const appliedFilters = [];
      if (filters.service_id) {
        const service = userServices.find(s => s.id.toString() === filters.service_id);
        if (service) appliedFilters.push(`Serviço: ${service.name} (Ramal: ${service.extension})`);
      } else {
        appliedFilters.push('Serviço: Todos');
      }

      if (filters.direction) {
        appliedFilters.push(`Direção: ${filters.direction === 'inbound' ? 'Entrada' : 'Saída'}`);
      } else {
        appliedFilters.push('Direção: Todas');
      }

      if (filters.disposition) {
        const dispositionMap = {
          'ANSWERED': 'Atendida',
          'NO ANSWER': 'Não atendida',
          'BUSY': 'Ocupado',
          'FAILED': 'Falhou',
          'CONGESTION': 'Congestionada'
        };
        appliedFilters.push(`Status: ${dispositionMap[filters.disposition as keyof typeof dispositionMap] || filters.disposition}`);
      } else {
        appliedFilters.push('Status: Todos');
      }

      if (filters.start_date) appliedFilters.push(`Data Inicial: ${filters.start_date}`);
      if (filters.end_date) appliedFilters.push(`Data Final: ${filters.end_date}`);

      // Filtros aplicados e resumo
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('FILTROS APLICADOS', 20, yPosition);

      yPosition += 12;

      // Resumo em destaque
      doc.setFillColor(240, 248, 255);
      doc.rect(20, yPosition - 5, pageWidth - 40, 15, 'F');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(`Total de registros exportados: ${reports?.length || 0}`, 25, yPosition + 2);
      doc.text(`Filtros aplicados: ${appliedFilters.length}`, pageWidth - 80, yPosition + 2, { align: 'right' });

      yPosition += 20;

      // Filtros lado a lado
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);

      // Organizar filtros em duas colunas
      const midPoint = Math.ceil(appliedFilters.length / 2);
      const leftColumn = appliedFilters.slice(0, midPoint);
      const rightColumn = appliedFilters.slice(midPoint);

      leftColumn.forEach((filter, index) => {
        doc.text(`• ${filter}`, 25, yPosition + (index * 6));
      });

      rightColumn.forEach((filter, index) => {
        doc.text(`• ${filter}`, pageWidth / 2 + 10, yPosition + (index * 6));
      });

      yPosition += Math.max(leftColumn.length, rightColumn.length) * 6 + 15;

      // Tabela de relatórios
      if (reports && reports.length > 0) {
        doc.setTextColor(40, 40, 40);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`RELATÓRIOS DE CHAMADAS (${reports.length} registros)`, 20, yPosition);

        yPosition += 10;

        // Preparar dados para a tabela
        const tableData = reports.map(report => [
          formatDateTime(report.timestamp),
          report.direction === 'inbound' ? 'Entrada' : 'Saída',
          report.caller_num || 'N/A',
          report.caller_name || '',
          report.callee_did || 'N/A',
          formatDuration(report.billsec),
          report.ring_time > 0 ? formatDuration(report.ring_time) : '0:00',
          report.disposition === 'ANSWERED' ? 'Atendida' :
          report.disposition === 'NO ANSWER' ? 'Não atendida' :
          report.disposition === 'BUSY' ? 'Ocupado' :
          report.disposition === 'FAILED' ? 'Falhou' :
          report.disposition === 'CONGESTION' ? 'Congestionada' :
          report.disposition,
          report.service ? `${report.service.name} (${report.service.extension})` : 'Não vinculado'
        ]);

        // Adicionar tabela com design melhorado
        autoTable(doc, {
          startY: yPosition,
          head: [['Data/Hora', 'Direção', 'Chamador', 'Nome', 'Destinatário', 'Duração', 'Toque', 'Status', 'Serviço']],
          body: tableData,
          theme: 'striped',
          styles: {
            fontSize: 7,
            cellPadding: 3,
            lineColor: [200, 200, 200],
            lineWidth: 0.1,
          },
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: 255,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center',
          },
          bodyStyles: {
            textColor: [40, 40, 40],
            fontSize: 7,
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 25 }, // Data/Hora
            1: { cellWidth: 15, halign: 'center' }, // Direção
            2: { cellWidth: 20, halign: 'center' }, // Chamador
            3: { cellWidth: 25 }, // Nome
            4: { cellWidth: 20, halign: 'center' }, // Destinatário
            5: { cellWidth: 15, halign: 'center' }, // Duração
            6: { cellWidth: 15, halign: 'center' }, // Toque
            7: { cellWidth: 20, halign: 'center' }, // Status
            8: { cellWidth: 25 }, // Serviço
          },
          margin: { left: 15, right: 15 },
          didDrawPage: function(data: any) {
            // Adicionar borda na página
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
          }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(12);
        doc.setTextColor(150, 150, 150);
        doc.text('Nenhum relatório encontrado com os filtros aplicados.', 20, yPosition);
        yPosition += 20;
      }

      // Rodapé elegante
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);


        // Linha separadora do rodapé
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(20, pageHeight - 30, pageWidth - 20, pageHeight - 30);

        // Texto do rodapé
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Página ${i} de ${totalPages}`, pageWidth - 30, pageHeight - 20);
        doc.text('Sistema de Relatórios PABX - Gerado automaticamente', 20, pageHeight - 20);
        doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 20, pageHeight - 15);
      }

      // Salvar o PDF
      const fileName = `relatorio-ligacoes-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      // Fechar loading e mostrar sucesso
      toast.dismiss(loadingToast);
      toast.success(`Relatório PDF gerado com sucesso! (${reports?.length || 0} registros)`, {
        duration: 4000,
        icon: '📄',
      });

    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(`Erro ao gerar PDF: ${error.message || 'Tente novamente'}`, {
        duration: 5000,
      });
    }
  };

  const fetchReports = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };

      const response = await callReportsService.getCallReports(params);

      // Handle different response structures
      let reportsData = [];
      let paginationData = {};
      let servicesData = [];

      if (response.data?.data?.reports) {
        // Structure: { data: { reports: [], pagination: {} } }
        reportsData = response.data.data.reports || [];
        paginationData = response.data.data.pagination || {};
        servicesData = response.data.data.userServices || [];
      } else if (response.data?.reports) {
        // Structure: { reports: [], pagination: {} }
        reportsData = response.data.reports || [];
        paginationData = response.data.pagination || {};
        servicesData = response.data.userServices || [];
      }

      setReports(reportsData);
      setTotalPages((paginationData as any).pages || 1);
      setTotalRecords((paginationData as any).total || 0);
      setUserServices(servicesData);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
      setReports([]);
      setTotalPages(1);
      setTotalRecords(0);
      setUserServices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );

      const response = await callReportsService.getStatistics(params);
      setStatistics(response.data?.statistics || null);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      setStatistics(null);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await callReportsService.getDashboardStatistics();
      // Tentar diferentes estruturas de resposta
      const stats = response.data?.data?.dashboard_statistics ||
                   response.data?.dashboard_statistics ||
                   null;

      // Se não há dados, usar valores padrão
      const defaultStats = {
        calls_this_month: 0,
        calls_this_year: 0,
        calls_with_duration: 0,
        total_duration_month_seconds: 0,
        total_duration_year_seconds: 0,
        answered_calls_month: 0,
        answered_calls_year: 0,
        answer_rate_month: 0,
        answer_rate_year: 0,
        current_month: new Date().toISOString().split('T')[0],
        current_year: new Date().getFullYear()
      };

      setDashboardStats(stats || defaultStats);
    } catch (error) {
      console.error('Erro ao buscar estatísticas do dashboard:', error);
      // Em caso de erro, usar valores padrão
      const defaultStats = {
        calls_this_month: 0,
        calls_this_year: 0,
        calls_with_duration: 0,
        total_duration_month_seconds: 0,
        total_duration_year_seconds: 0,
        answered_calls_month: 0,
        answered_calls_year: 0,
        answer_rate_month: 0,
        answer_rate_year: 0,
        current_month: new Date().toISOString().split('T')[0],
        current_year: new Date().getFullYear()
      };
      setDashboardStats(defaultStats);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchStatistics();
    fetchDashboardStats();
  }, []);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    fetchReports(page);
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getDispositionColor = (disposition: string): string => {
    switch (disposition) {
      case 'ANSWERED':
        return 'text-green-600 bg-green-100';
      case 'NO ANSWER':
        return 'text-red-600 bg-red-100';
      case 'BUSY':
        return 'text-yellow-600 bg-yellow-100';
      case 'FAILED':
        return 'text-gray-600 bg-gray-100';
      case 'CONGESTION':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getDirectionIcon = (direction: string): string => {
    return direction === 'inbound' ? '📥' : '📤';
  };

  if (loading && (!reports || reports.length === 0)) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Relatórios de Chamadas</h1>
              <p className="mt-2 text-lg text-gray-600">
                Visualize e analise os relatórios de chamadas recebidos via PABX
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
                <div className="text-sm text-gray-500">Total de Registros</div>
                <div className="text-2xl font-bold text-gray-900">{totalRecords}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard de Ligações */}
        <div className="mt-8">
          {/* Header do Dashboard */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                    <BarChart3Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Dashboard de Ligações</h2>
                    <p className="text-sm text-gray-600 mt-1">Métricas e estatísticas das suas chamadas PABX</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="mt-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* Ligações do Mês */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                    <CalendarIcon className="h-6 w-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Ligações do Mês</p>
                    <p className="text-2xl font-bold text-gray-900">{(dashboardStats?.calls_this_month || 0).toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {dashboardStats?.answered_calls_month || 0} atendidas ({dashboardStats?.answer_rate_month || 0}%)
                    </p>
                  </div>
                </div>
              </div>

              {/* Ligações do Ano */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                    <TrendingUpIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Ligações do Ano</p>
                    <p className="text-2xl font-bold text-gray-900">{(dashboardStats?.calls_this_year || 0).toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {dashboardStats?.answered_calls_year || 0} atendidas ({dashboardStats?.answer_rate_year || 0}%)
                    </p>
                  </div>
                </div>
              </div>

              {/* Ligações Efetivas */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                    <PhoneCallIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Ligações Efetivas</p>
                    <p className="text-2xl font-bold text-gray-900">{(dashboardStats?.calls_with_duration || 0).toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Com duração {'>'}5s (excluindo toques)
                    </p>
                  </div>
                </div>
              </div>

              {/* Tempo Total do Mês */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                    <ClockIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Tempo Total (Mês)</p>
                    <p className="text-2xl font-bold text-gray-900">{formatDuration(dashboardStats?.total_duration_month_seconds || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Duração das conversas
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Sections */}
          <div className="mt-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Monthly Performance */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 px-8 py-6 border-b border-white/30">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                      <CheckCircleIcon className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Performance do Mês</h3>
                      <p className="text-sm text-gray-600 mt-1">Métricas detalhadas do período atual</p>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Total de Ligações</span>
                      <span className="text-lg font-bold text-gray-900">{(dashboardStats?.calls_this_month || 0).toLocaleString('pt-BR')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Ligações Atendidas</span>
                      <span className="text-lg font-bold text-green-600">{dashboardStats?.answered_calls_month || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Taxa de Atendimento</span>
                      <span className="text-lg font-bold text-blue-600">{dashboardStats?.answer_rate_month || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Tempo Total</span>
                      <span className="text-lg font-bold text-purple-600">{formatDuration(dashboardStats?.total_duration_month_seconds || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Yearly Overview */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-8 py-6 border-b border-white/30">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                      <TrendingUpIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Visão Anual</h3>
                      <p className="text-sm text-gray-600 mt-1">Performance acumulada do ano</p>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Total de Ligações</span>
                      <span className="text-lg font-bold text-gray-900">{(dashboardStats?.calls_this_year || 0).toLocaleString('pt-BR')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Ligações Atendidas</span>
                      <span className="text-lg font-bold text-green-600">{dashboardStats?.answered_calls_year || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Taxa de Atendimento</span>
                      <span className="text-lg font-bold text-blue-600">{dashboardStats?.answer_rate_year || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Tempo Total</span>
                      <span className="text-lg font-bold text-purple-600">{formatDuration(dashboardStats?.total_duration_year_seconds || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Filtros</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-7">
              <div>
                <label htmlFor="service_id" className="block text-sm font-medium text-gray-700">
                  Serviço
                </label>
                <select
                  id="service_id"
                  value={filters.service_id}
                  onChange={(e) => handleFilterChange('service_id', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">Todos os serviços</option>
                  {userServices.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} (Ramal: {service.extension})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="direction" className="block text-sm font-medium text-gray-700">
                  Direção
                </label>
                <select
                  id="direction"
                  value={filters.direction}
                  onChange={(e) => handleFilterChange('direction', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">Todas</option>
                  <option value="inbound">Entrada</option>
                  <option value="outbound">Saída</option>
                </select>
              </div>

              <div>
                <label htmlFor="disposition" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="disposition"
                  value={filters.disposition}
                  onChange={(e) => handleFilterChange('disposition', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">Todos</option>
                  <option value="ANSWERED">Atendida</option>
                  <option value="NO ANSWER">Não atendida</option>
                  <option value="BUSY">Ocupado</option>
                  <option value="FAILED">Falhou</option>
                  <option value="CONGESTION">Congestionada</option>
                </select>
              </div>

              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                  Data Inicial
                </label>
                <input
                  type="date"
                  id="start_date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                  Data Final
                </label>
                <input
                  type="date"
                  id="end_date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="flex items-end space-x-3 lg:col-span-2">
                <button
                  onClick={() => setFilters({
                    direction: '',
                    disposition: '',
                    start_date: '',
                    end_date: '',
                    service_id: ''
                  })}
                  className="flex-1 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Limpar Filtros
                </button>
                <button
                  onClick={exportToPDF}
                  disabled={loading}
                  className="flex-1 inline-flex justify-center items-center py-2 px-4 border border-blue-500 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  Exportar PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de Relatórios */}
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Relatórios de Chamadas ({totalRecords || 0} registros)
            </h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner />
            </div>
          ) : (!reports || reports.length === 0) ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <p className="text-lg">Nenhum relatório encontrado</p>
                <p className="text-sm mt-2">Tente ajustar os filtros ou aguarde novos dados do PABX</p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data/Hora
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Direção
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Chamador
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Destinatário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duração
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Serviço
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(reports || []).map((report) => (
                      <tr key={report.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateTime(report.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center">
                            <span className="mr-2">{getDirectionIcon(report.direction)}</span>
                            {report.direction === 'inbound' ? 'Entrada' : 'Saída'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{report.caller_num || 'N/A'}</div>
                            {report.caller_name && report.caller_name !== report.caller_num && (
                              <div className="text-gray-500 text-xs">{report.caller_name}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {report.callee_did || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div>{formatDuration(report.billsec)}</div>
                            {report.ring_time > 0 && (
                              <div className="text-gray-500 text-xs">Toque: {formatDuration(report.ring_time)}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDispositionColor(report.disposition)}`}>
                            {report.disposition === 'ANSWERED' ? 'Atendida' :
                             report.disposition === 'NO ANSWER' ? 'Não atendida' :
                             report.disposition === 'BUSY' ? 'Ocupado' :
                             report.disposition === 'FAILED' ? 'Falhou' :
                             report.disposition === 'CONGESTION' ? 'Congestionada' :
                             report.disposition}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {report.service ? (
                            <div>
                              <div className="font-medium">{report.service.name}</div>
                              <div className="text-gray-500 text-xs">Ramal: {report.service.extension}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400">Não vinculado</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {(reports || []).map((report) => (
                  <div key={report.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <span className="mr-2">{getDirectionIcon(report.direction)}</span>
                        <span className="text-sm font-medium text-gray-900">
                          {report.direction === 'inbound' ? 'Entrada' : 'Saída'}
                        </span>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDispositionColor(report.disposition)}`}>
                        {report.disposition === 'ANSWERED' ? 'Atendida' :
                         report.disposition === 'NO ANSWER' ? 'Não atendida' :
                         report.disposition === 'BUSY' ? 'Ocupado' :
                         report.disposition === 'FAILED' ? 'Falhou' :
                         report.disposition === 'CONGESTION' ? 'Congestionada' :
                         report.disposition}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="text-xs text-gray-500">{formatDateTime(report.timestamp)}</div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">Chamador</div>
                          <div className="text-sm font-medium">{report.caller_num || 'N/A'}</div>
                          {report.caller_name && report.caller_name !== report.caller_num && (
                            <div className="text-xs text-gray-500">{report.caller_name}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Destinatário</div>
                          <div className="text-sm font-medium">{report.callee_did || 'N/A'}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">Duração</div>
                          <div className="text-sm font-medium">{formatDuration(report.billsec)}</div>
                          {report.ring_time > 0 && (
                            <div className="text-xs text-gray-500">Toque: {formatDuration(report.ring_time)}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Serviço</div>
                          {report.service ? (
                            <div>
                              <div className="text-sm font-medium">{report.service.name}</div>
                              <div className="text-xs text-gray-500">Ramal: {report.service.extension}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Não vinculado</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginação */}
              {totalPages && totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange((currentPage || 1) - 1)}
                      disabled={(currentPage || 1) === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => handlePageChange((currentPage || 1) + 1)}
                      disabled={(currentPage || 1) === (totalPages || 1)}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando página <span className="font-medium">{currentPage || 1}</span> de{' '}
                        <span className="font-medium">{totalPages || 1}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange((currentPage || 1) - 1)}
                          disabled={(currentPage || 1) === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Anterior</span>
                          ‹
                        </button>
                        {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min((totalPages || 1) - 4, currentPage - 2)) + i;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === (currentPage || 1)
                                  ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handlePageChange((currentPage || 1) + 1)}
                          disabled={(currentPage || 1) === (totalPages || 1)}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Próximo</span>
                          ›
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallReportsPage;